import _extends from "@babel/runtime/helpers/esm/extends";
var __awaiter = this && this.__awaiter || function (thisArg, _arguments, P, generator) {
  function adopt(value) {
    return value instanceof P ? value : new P(function (resolve) {
      resolve(value);
    });
  }
  return new (P || (P = Promise))(function (resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }
    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }
    function step(result) {
      result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
    }
    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import { Components as PopoverComponents } from './Popover.style';
import useOnClickOutside from 'use-onclickoutside';
import { useFloating, autoUpdate, offset, flip, shift, limitShift, size, detectOverflow, arrow } from '@floating-ui/react-dom';
const Popover = props => {
  const {
    children,
    content,
    open = false,
    trigger = 'click',
    position = 'top-center',
    maxwidth,
    maxheight,
    width = 'auto',
    hasArrow = false,
    preventcloseonblur = false,
    onShow,
    onHide,
    fit = 'medium'
  } = props;
  const positionMap = {
    'bottom-center': 'bottom',
    'bottom-left': 'bottom-start',
    'bottom-right': 'bottom-end',
    'left-bottom': 'left-end',
    'left-center': 'left',
    'left-top': 'left-start',
    'right-bottom': 'right-end',
    'right-center': 'right',
    'right-top': 'right-start',
    'top-center': 'top',
    'top-left': 'top-start',
    'top-right': 'top-end'
  };
  const [isOpen, setIsOpen] = useState(open);
  const [hoverTimeout, setHoverTimeout] = useState();
  const [latestClickTarget, setLatestClickTarget] = useState();
  const arrowRef = useRef(null);
  let latestOverflow = 0;
  const onDocumentMousedown = e => {
    setLatestClickTarget(e.target);
  };
  useEffect(() => {
    document.addEventListener('mousedown', onDocumentMousedown, true);
    return () => {
      document.removeEventListener('mousedown', onDocumentMousedown, true);
    };
  });
  useEffect(() => {
    if (open) {
      togglePopover(undefined, true);
    } else {
      setIsOpen(false);
    }
  }, [position, maxheight, maxwidth, trigger, open]);
  const detectOverflowMiddleware = {
    name: 'detectOverflowMiddleware',
    fn: state => __awaiter(void 0, void 0, void 0, function* () {
      const {
        top,
        bottom
      } = yield detectOverflow(state, {
        padding: 8
      });
      if (top > 0) {
        latestOverflow = top;
        const newHeight = state.rects.floating.height - top;
        if (refs.floating && refs.floating.current) {
          const el = refs.floating.current.querySelector('.popover-content');
          if (el) {
            el.style.maxHeight = "".concat(newHeight, "px");
          }
        }
        return {};
      }
      if (bottom > 0) {
        latestOverflow = bottom;
        const newHeight = state.rects.floating.height - bottom;
        if (refs.floating && refs.floating.current) {
          const el = refs.floating.current.querySelector('.popover-content');
          if (el) {
            el.style.maxHeight = "".concat(newHeight, "px");
          }
        }
        return {};
      }
      if (latestOverflow > 0 && top < 0 && bottom < 0) {
        if (refs.floating && refs.floating.current) {
          const el = refs.floating.current.querySelector('.popover-content');
          if (el) {
            el.style.maxHeight = maxheight || 'none';
          }
        }
      }
      return {};
    })
  };
  const getArrowPadding = () => {
    const arrowPadding = {
      small: 6,
      medium: 7,
      large: 8
    };
    return arrowPadding[fit];
  };
  const {
    x,
    y,
    refs,
    placement,
    middlewareData
  } = useFloating({
    placement: positionMap[position],
    open: isOpen,
    middleware: [offset({
      mainAxis: 8,
      crossAxis: 0
    }), flip(), shift({
      limiter: limitShift({
        offset: _ref => {
          let {
            rects,
            placement
          } = _ref;
          return placement.startsWith('top') || placement.startsWith('bottom') ? rects.reference.width / 2 : rects.reference.height / 2;
        }
      })
    }), detectOverflowMiddleware, size({
      apply: _ref2 => {
        let {
          elements,
          rects
        } = _ref2;
        _extends(elements.floating.style, {
          maxWidth: maxwidth || 'none',
          width: width === 'trigger' ? "".concat(rects.reference.width, "px") : width
        });
      }
    }), hasArrow ? arrow({
      element: arrowRef,
      padding: getArrowPadding()
    }) : null],
    whileElementsMounted() {
      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }
      const cleanup = autoUpdate.apply(void 0, args.concat([{
        animationFrame: true
      }]));
      // Important! Always return the cleanup function.
      return cleanup;
    }
  });
  useOnClickOutside(refs.floating, event => {
    const reference = refs.reference.current;
    if (reference && !reference.contains(event.target) && isOpen) {
      togglePopover(undefined, false);
    }
  });
  const togglePopover = (e, value) => {
    setIsOpen(value ? value : false);
    if (value && typeof onShow === 'function') {
      onShow(e);
    } else if (!value && typeof onHide === 'function') {
      onHide(e);
    }
  };
  const handleMouseEnter = () => {
    clearTimeout(hoverTimeout);
    setHoverTimeout(setTimeout(() => {
      if (trigger === 'hover' || trigger === 'hover focus') {
        togglePopover(undefined, true);
      }
    }, 50));
  };
  const handleMouseLeave = () => {
    clearTimeout(hoverTimeout);
    setHoverTimeout(setTimeout(() => {
      if (trigger === 'hover' || trigger === 'hover focus') {
        togglePopover(undefined, false);
      }
    }, 200));
  };
  const getEventProps = () => {
    const eventProps = {};
    switch (trigger) {
      case 'click':
        eventProps.onClick = e => togglePopover(e, !isOpen);
        break;
      case 'hover focus':
        eventProps.onFocus = e => togglePopover(e, true);
        eventProps.onBlur = e => togglePopover(e, false);
        break;
      case 'focus':
        eventProps.onFocus = e => togglePopover(e, true);
        eventProps.onBlur = e => togglePopover(e, false);
        break;
      default:
        break;
    }
    return eventProps;
  };
  const isTargetInComponent = target => {
    if (!refs.floating) {
      return;
    }
    const floating = refs.floating.current;
    if (floating) {
      return floating.contains(target);
    }
  };
  const handleOnBlur = e => {
    if (preventcloseonblur) {
      return;
    }
    if (e.relatedTarget && isTargetInComponent(e.relatedTarget) || latestClickTarget && isTargetInComponent(latestClickTarget)) {
      setLatestClickTarget(null);
      return;
    }
    togglePopover(e, false);
  };
  return _jsxs(PopoverComponents.PopoverContainer, _extends({
    onBlur: e => handleOnBlur(e),
    "data-position": placement,
    fit: fit,
    ref: refs.setReference,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave
  }, {
    children: [_jsx("div", _extends({}, getEventProps(), {
      children: Array.isArray(children) ? children[0] : children
    })), (content || Array.isArray(children) && children.length === 2) && isOpen && _jsxs(PopoverComponents.PopoverContentWrapper, _extends({
      ref: refs.setFloating,
      top: y,
      left: x,
      style: {
        maxHeight: maxheight || 'none'
      }
    }, {
      children: [_jsx(PopoverComponents.PopoverContent, _extends({
        style: {
          maxWidth: maxwidth || 'none',
          maxHeight: "calc(".concat(maxheight, " - 1px * 4)")
        },
        className: "popover-content"
      }, {
        children: Array.isArray(children) && children.length === 2 ? children[1] : content
      })), hasArrow && _jsx(PopoverComponents.Arrow, {
        ref: arrowRef,
        top: middlewareData.arrow ? middlewareData.arrow.y : 0,
        left: middlewareData.arrow ? middlewareData.arrow.x : 0,
        className: "arrow",
        fit: fit,
        placement: placement.split('-')[0],
        staticSide: {
          top: 'bottom',
          right: 'left',
          bottom: 'top',
          left: 'right'
        }[placement.split('-')[0]]
      })]
    }))]
  }));
};
export default Popover;